import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import AvatarEditor from "react-avatar-editor";
import ReactCrop from "react-image-crop";
import styles from "./PhotoCrop.module.scss";
import Range from "../Inputs/Range";
import Button from "../Button";
import { getCroppedImg, getImgSizes } from "../../utils";
import Footer from "../Footer";

const MIN_IMG_WIDTH = 310;
const MIN_IMG_HEIGHT = 310;
const MIN_CANVAS_WIDTH = 1080;
const MIN_CANVAS_HEIGHT = 1080;

const PhotoCrop = ({
  onCloseCrop,
  onSaveCrop,
  image,
  isCircled,
  isPost,
  withCrop,
  modalTitle = "PROFILE PICTURE",
  modalSubtitle = "Resize and adjust your picture before saving.",
}) => {
  const [zoom, setZoom] = useState(1);
  const [coefficient, setCoefficient] = useState(1);
  const [position, setPosition] = useState({ x: 0.5, y: 0.5 });
  const [edited, setEdited] = useState(null);
  const [editorSize, setEditorSize] = useState(240);
  const [sliderValue, setSliderValue] = useState(1);

  const [crop, setCrop] = useState({
    aspect: isPost ? "" : 1,
    width: MIN_IMG_WIDTH,
    height: MIN_IMG_HEIGHT,
    x: 0,
    y: 0,
  });
  const [resultCrop, setResultCrop] = useState(null);
  const [currentImage, setCurrentImage] = useState(null);
  const [currentCropSize, setCurrentCropSize] = useState(null);

  useEffect(() => {
    const imageRef = new Image();
    imageRef.src = image;

    const calculate = (width, height) => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const { imgWidth, imgHeight } = getImgSizes(
        width,
        height,
        MIN_IMG_WIDTH,
        MIN_IMG_HEIGHT
      );
      const hRatio = MIN_IMG_WIDTH / imgWidth;
      const vRatio = MIN_IMG_HEIGHT / imgHeight;
      const ratio = Math.min(hRatio, vRatio);
      const hQualityRatio = MIN_CANVAS_WIDTH / imgWidth;
      const vQualityRatio = MIN_CANVAS_HEIGHT / imgHeight;
      const qualityRatio = Math.min(hQualityRatio, vQualityRatio);
      canvas.width = Number((imgWidth * qualityRatio).toFixed(0));
      canvas.height = Number((imgHeight * qualityRatio).toFixed(0));
      const newImage = new Image(
        Number((imgWidth * ratio).toFixed(0)),
        Number((imgHeight * ratio).toFixed(0))
      );
      newImage.src = image;
      setCurrentCropSize({
        width: imgWidth * ratio,
        height: imgHeight * ratio,
      });
      newImage.addEventListener("load", () => {
        ctx.drawImage(
          newImage,
          0,
          0,
          imgWidth,
          imgHeight,
          0,
          0,
          imgWidth * qualityRatio,
          imgHeight * qualityRatio
        );
        canvas.toBlob((blob) => {
          const url = URL.createObjectURL(blob);
          setCurrentImage(url);
        });
      });
    };

    const listener = () => {
      calculate(imageRef.width, imageRef.height);
    };

    imageRef.addEventListener("load", listener);
    return () => {
      imageRef.removeEventListener("load", listener);
    };
  }, [image]);

  useEffect(() => {
    if (!isPost) {
      setCrop({
        aspect: 1,
        width: MIN_IMG_WIDTH,
        height: MIN_IMG_HEIGHT,
        x: 0,
        y: 0,
      });
    } else if (currentCropSize) {
      setCrop({
        aspect: "",
        width: currentCropSize.width,
        height: currentCropSize.height,
        x: 0,
        y: 0,
      });
    }

    document.body.style.position = "relative";
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.position = "static";
      document.documentElement.style.overflow = "initial";
      document.body.style.overflow = "initial";
    };
  }, [isPost, currentCropSize]);

  const onClickSave = () => {
    const getCroppedImage = async (image) => {
      let width = 310,
        height = 310;
      if (isPost) {
        width = currentCropSize.width;
        height = currentCropSize.height;
      }
      const result1 = await getCroppedImg(
        image,
        resultCrop || crop,
        width,
        height,
        "image/jpeg"
      );
      const result2 = await getCroppedImg(
        image,
        resultCrop || crop,
        width,
        height,
        "image/webp"
      );
      onSaveCrop({
        tosave: result1,
        topopup: result2,
      });
    };
    if (withCrop && edited && (resultCrop || crop)) {
      getCroppedImage(edited.getImage());
    } else if (edited) {
      edited.getImage().toBlob((blob) => {
        const imageData = {
          tosave: blob,
          topopup: blob,
        };
        onSaveCrop(imageData);
      });
    }
  };

  const setEditorRef = (editor) => setEdited(editor);

  const zoomChange = (value) => {
    setSliderValue(value);
    const scale = value * coefficient;
    setZoom(scale);
  };
  
  const handlePositionChange = (pos) => {
    setPosition(pos);
  };
  const getStartCoefficient = (imgInfo) => {
    let scale = 1;
    if (imgInfo.height > imgInfo.width) {
      const scaleCoef = 1 / (imgInfo.height / imgInfo.width);
      scale = scaleCoef;
    } else {
      const scaleCoef = 1 / (imgInfo.width / imgInfo.height);
      scale = scaleCoef;
    }
    setCoefficient(scale);
  };
  useEffect(() => {
    setZoom(coefficient);
    setSliderValue(1);
  }, [coefficient]);

  useEffect(() => {
    const updateEditorSize = () => {
      if (window.innerWidth <= 600) {
        setEditorSize(200);
      } else {
        setEditorSize(240);
      }
    };
    
    updateEditorSize();
    window.addEventListener('resize', updateEditorSize);
    return () => window.removeEventListener('resize', updateEditorSize);
  }, []);

  const renderPortal = (content) => {
    if (typeof document !== "undefined") {
      return createPortal(content, document.body);
    }
    return content;
  };

  if (!isPost && withCrop) {
    return renderPortal(
      <div className={styles.PhotoCrop}>
        <div className={styles.panel}>
          <div className={styles.header}>
            <div className={styles.title}>{modalTitle}</div>
            <div className={styles.subtitle}>{modalSubtitle}</div>
          </div>
          <div className={`${styles.editorWrap} ${isCircled ? styles.isCircled : ''}`}>
            <AvatarEditor
              ref={setEditorRef}
              className={styles.imageCroper}
              width={editorSize}
              height={editorSize}
              image={image}
              border={0}
              borderRadius={isCircled ? editorSize / 2 : 0}
              scale={Number(zoom)}
              position={position}
              onPositionChange={handlePositionChange}
              onLoadSuccess={getStartCoefficient}
              disableBoundaryChecks={false}
            />
            {isCircled && <div className={styles.circleGrid} aria-hidden />}
          </div>
          <div className={styles.zoomControls}>
            <button type="button" className={`${styles.zoomBtn} ${styles.minus}`} onClick={() => zoomChange(Math.max(1, parseFloat(sliderValue) - 0.5))} aria-label="Zoom out"></button>
            <div className={styles.zoom}> 
              <Range min={1} max={20} step={0.5} value={sliderValue} onChange={zoomChange} variant="photoCrop" />
            </div>
            <button type="button" className={`${styles.zoomBtn} ${styles.plus}`} onClick={() => zoomChange(Math.min(20, parseFloat(sliderValue) + 0.5))} aria-label="Zoom in"></button>
          </div>
          <div className={styles.actions}>
            <Button
              className={styles.save}
              color="blue"
              size="large"
              type="button"
              onClick={onClickSave}
            >
              SAVE
            </Button>
            <Button
              className={styles.cancel}
              color="transparent"
              size="large"
              type="button"
              onClick={onCloseCrop}
            >
              Cancel
            </Button>
          </div>
          <Footer className={styles.modalFooter} />
        </div>
      </div>
    );
  }
  return renderPortal(
    <div className={styles.PhotoCrop}>
      <div className={styles.panel}>
        <div className={styles.header}>
          <div className={styles.title}>{modalTitle}</div>
          <div className={styles.subtitle}>{modalSubtitle}</div>
        </div>
        {currentCropSize && (
          <div className={styles.container}>
            {withCrop ? (
              <ReactCrop
                src={image}
                crop={crop}
                onChange={(crop) => setCrop(crop)}
                onComplete={(crop) => setResultCrop(crop)}
                className={styles.crop}
                minWidth="39"
                minHeight="39"
                keepSelection={true}
                renderComponent={
                  <div className={`${styles.editorWrap} ${isCircled ? styles.isCircled : ''}`} style={{ height: editorSize }}>
                    <AvatarEditor
                      ref={setEditorRef}
                      className={styles.imageCroper}
                      width={editorSize}
                      height={editorSize}
                      image={currentImage}
                      border={0}
                      borderRadius={isCircled ? editorSize / 2 : 0}
                      scale={Number(zoom)}
                      position={position}
                      onPositionChange={handlePositionChange}
                      disableBoundaryChecks={false}
                    />
                    {isCircled && <div className={styles.circleGrid} aria-hidden />}
                  </div>
                }
              />
            ) : (
              <div className={`${styles.editorWrap} ${isCircled ? styles.isCircled : ''}`}>
                <AvatarEditor
                  ref={setEditorRef}
                  className={styles.imageCroper}
                  width={editorSize}
                  height={editorSize}
                  image={image}
                  border={0}
                  borderRadius={isCircled ? editorSize / 2 : 0}
                  scale={Number(zoom)}
                  position={position}
                  onPositionChange={handlePositionChange}
                  disableBoundaryChecks={false}
                />
                {isCircled && <div className={styles.circleGrid} aria-hidden />}
              </div>
            )}
          </div>
        )}
        <div className={styles.zoomControls}>
          <button type="button" className={`${styles.zoomBtn} ${styles.minus}`} onClick={() => zoomChange(Math.max(1, parseFloat(sliderValue) - 0.5))} aria-label="Zoom out"></button>
          <div className={styles.zoom}>
            <Range min={1} max={20} step={0.5} value={sliderValue} onChange={zoomChange} variant="photoCrop" />
          </div>
          <button type="button" className={`${styles.zoomBtn} ${styles.plus}`} onClick={() => zoomChange(Math.min(20, parseFloat(sliderValue) + 0.5))} aria-label="Zoom in"></button>
        </div>
        <div className={styles.actions}>
          <Button
            className={styles.save}
            color="blue"
            size="large"
            type="button"
            onClick={onClickSave}
          >
            SAVE
          </Button>
          <Button
            className={styles.cancel}
            color="transparent"
            size="large"
            type="button"
            onClick={onCloseCrop}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PhotoCrop;
